/*
 * NOTE: We want to maintain this component level template as .ts file so we can add comments.
 */

export enum LegendPlacement {
  BOTTOM = 'bottom',
  RIGHT = 'right',
}

export default {
  name: 'dashify:Pie',
  title: 'Pie',
  owner: 'System',
  solution: '',
  tags: ['data', 'visualization', 'pie', 'donut'],
  createdAt: 'May 30, 2023, 10:14 AM',
  updatedAt: 'Oct 11, 2023, 03:32 PM',
  spec: {
    props: {
      highchartsConfig: {
        legend: {
          enabled: false,
        },
      },
      // Non Highcharts-related API
      customConfig: {
        legendPlacement: LegendPlacement.RIGHT,
        dataLabelsFormat: {
          valueOnly: false,
        },
      },
    },
    // Highcharts config for legend placement based on Particle
    legendPlacements: {
      bottom: {
        legend: {
          align: 'left',
          layout: 'horizontal',
          verticalAlign: 'bottom',
        },
      },
      right: {
        legend: {
          align: 'right',
          layout: 'vertical',
          verticalAlign: 'middle',
        },
      },
    },
    legendConfig: '/${ $lookup(legendPlacements, props.customConfig.legendPlacement) }',
    dataLabelsFormatDefault: '<div><span>{point.name}</span><span>&nbsp;(<b>{point.y:,0f}</b>)</span></div>',
    dataLabelsFormatValueOnly: '<div><span><b>{point.y:,0f}</b></span></div>',
    defaults: {
      series: [],
      chart: {
        type: 'pie',
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0,0,0,0)',
        style: {
          fontFamily: 'var(--typography-font-body)',
        },
        styledMode: false,
        events: {
          render: function (this: any) {
            if (this.series && this.series[0]?.points) {
              for (var i = 0; i < this.series[0].points.length; i++) {
                const checkboxSize = 12;
                const legendGroupX = this.legend.group?.alignAttr?.translateX;
                const legendItemX = this.legend.allItems[i]?.legendItem?.x;
                const legendGroupY = this.legend.group?.alignAttr?.translateY;
                const legendItemY = this.legend.allItems[i]?.legendItem.y;
                if (this.series[0].points[i].checkbox) {
                  this.series[0].points[i].checkbox.style.left = `${legendGroupX + legendItemX - checkboxSize}px`;
                  this.series[0].points[i].checkbox.style.top = `${legendGroupY + legendItemY + checkboxSize / 2}px`;
                  this.series[0].points[i].checkbox.style.visibility = 'hidden';
                }
              }
            }
          },
        },
      },
      colors: [
        'var(--color-accent-a)',
        'var(--color-accent-b)',
        'var(--color-accent-c)',
        'var(--color-accent-d)',
        'var(--color-accent-e)',
        'var(--color-accent-f)',
        'var(--color-accent-g)',
        'var(--color-accent-h)',
        'var(--color-accent-i)',
        'var(--color-accent-j)',
      ],
      title: {
        style: {
          color: 'var(--color-default-text-strong)',
          fontFamily: 'var(--typography-font-body)',
          fontSize: '28px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 33,
          textShadow: false,
        },
        x: '/${ props.highchartsConfig.legend.enabled ? -80 : 0 }',
        verticalAlign: 'middle',
      },
      subtitle: {
        style: {
          color: 'var(--color-default-text-medium)',
          fontFamily: 'var(--typography-font-body)',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 19,
        },
        verticalAlign: 'middle',
      },
      plotOptions: {
        pie: {
          // below following https://api.highcharts.com/highcharts/plotOptions.pie.borderWidth
          // in hope to get rid of the white border around slices but did not work
          borderWidth: 0.5,
          borderColor: '#FFF',
          borderRadius: 0,
          size: '100%',
          allowPointSelect: true,
          cursor: 'pointer',
          innerSize: '56.25%',
          crisp: true,
          showCheckbox: true,
          dataLabels: {
            enabled: false,
            format:
              '/${ props.customConfig.dataLabelsFormat.valueOnly ? dataLabelsFormatValueOnly : dataLabelsFormatDefault }',
            style: {
              textOutline: 'none',
              fontFamily: 'var(--typography-font-body)',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: '400',
              color: 'var(--color-default-text-medium)',
            },
          },
          point: {
            events: {
              legendItemClick: function (this: any) {
                const allPoints = this.series.points;
                const point = this as Highcharts.Point & {
                  checkbox: { style: { visibility: string }; checked: boolean };
                };

                // toggle the visibility of the current point's checkbox
                const isVisible = point.checkbox.style.visibility === 'visible';
                point.checkbox.style.visibility = isVisible ? 'hidden' : 'visible';
                point.checkbox.checked = !isVisible;

                // slice or unslice the current point based on its visibility
                this.slice(!isVisible, true, true);

                // iterate over all points to ensure only the current one can be sliced at a time
                allPoints.forEach((p: any) => {
                  if (p !== this) {
                    p.slice(false, true, true);
                    (
                      p as Highcharts.Point & { checkbox: { style: { visibility: string }; checked: boolean } }
                    ).checkbox.style.visibility = 'hidden';
                    (
                      p as Highcharts.Point & { checkbox: { style: { visibility: string }; checked: boolean } }
                    ).checkbox.checked = false;
                  }
                });
                return false; // prevent the default behavior
              },
              select: function (this: any) {
                const point = this as Highcharts.Point & {
                  checkbox: { style: { visibility: string }; checked: boolean };
                };

                // selection toggles similarly to legendItemClick
                const isVisible = point.checkbox.style.visibility === 'visible';
                point.checkbox.style.visibility = isVisible ? 'hidden' : 'visible';
                point.checkbox.checked = !isVisible;

                // slice or unslice the current point based on its visibility
                this.slice(!isVisible, true, true);

                // handle other points
                this.series.points.forEach((p: any) => {
                  if (p !== this) {
                    p.slice(false, true, true);
                    (
                      p as Highcharts.Point & { checkbox: { style: { visibility: string }; checked: boolean } }
                    ).checkbox.style.visibility = 'hidden';
                    (
                      p as Highcharts.Point & { checkbox: { style: { visibility: string }; checked: boolean } }
                    ).checkbox.checked = false;
                  }
                });
              },
            },
          },
        },
      },
      legend: {
        enabled: false,
        symbolRadius: 0,
        padding: 0,
        width: 150,
        itemStyle: {
          fontFamily: 'var(--typography-font-body)',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '18px',
          color: 'var(--color-default-text-medium)',
          backgroundColor: 'var(--color-default-bg)',
          borderWidth: 0,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
        itemHoverStyle: {
          color: 'var(--color-default-text)',
        },
        navigation: {
          activeColor: 'var(--color-default-text-medium)',
          inactiveColor: 'var(--color-default-text-medium-disabled)',
          arrowSize: 10,
          style: {
            fontFamily: 'var(--typography-font-body)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            color: 'var(--color-default-text-medium)',
          },
        },
        itemCheckboxStyle: {
          width: '12px',
          height: '12px',
        },
      },
      tooltip: {
        enabled: true,
        valueDecimals: 0,
        useHTML: true,
        outside: true,
        backgroundColor: 'var(--color-default-bg)',
        style: {
          fontFamily: 'var(--typography-font-body)',
          fontSize: '11px',
          fontStyle: 'normal',
          fontWeight: '400',
          color: 'var(--color-default-text)',
          backgroundColor: 'var(--color-default-bg)',
        },
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    },
    view: [['dashify:Highcharts', '/${ $defaultsDeep({}, legendConfig, props.highchartsConfig, defaults) }']],
  },
};
